import { doc, getDoc, getDocs, collection, setDoc, updateDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type MessageDefinition } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';

const MESSAGE_DEFINITIONS = 'messageDefinitions';

const converter = buildConverter<MessageDefinition>(
  (data) => data as MessageDefinition,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreMessageDefinitionRepository {
  async findAll(): Promise<MessageDefinition[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_DEFINITIONS),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<MessageDefinition | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, MESSAGE_DEFINITIONS, id).withConverter(converter));
    return snap.data();
  }

  async findByType(messageType: number): Promise<MessageDefinition | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_DEFINITIONS),
      where('messageType', '==', messageType),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs[0]?.data();
  }

  async save(def: MessageDefinition): Promise<MessageDefinition> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(def.id);
    const data = { ...def, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, MESSAGE_DEFINITIONS, def.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, MESSAGE_DEFINITIONS, def.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }
}

export const createFirestoreMessageDefinitionRepository = (): FirestoreMessageDefinitionRepository =>
  new FirestoreMessageDefinitionRepository();
